import Card from "common/Boxes/Card.tsx";
import Title from "common/Typography/Title.tsx";
import styled, { useTheme } from "styled-components";
import { Pencil } from "lucide-react";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import { CryptoCoins, PayPal } from "common/Icons/Icons.tsx";
import AntEditOutlined from "@ant-design/icons/EditOutlined";
import React, { useState } from "react";
import CreateOrEditPayoutMethodModal from "features/payouts/PayoutMethods/CreateOrEditPayoutMethodModal.tsx";
import Flex from "common/Boxes/Flex.tsx";
import { PAYOUT_SERVICE_DISPLAY_NAME } from "features/affiliation/common/constants.ts";
import { dateToMoment, formatMoment } from "@shared/utils/date.ts";
import { SmallText } from "features/affiliation/common/styles.ts";
import { useSelector } from "react-redux";
import { selectIsDarkMode } from "@shared/slices/common.slice.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import {
  LanguageNameSpaces,
  PaymentMethodType,
  PayoutMethod,
  PayoutMethodSource,
} from "@shared/types";
import { CRYPTO_ASSET_TO_DISPLAY } from "features/payouts/PayoutMethods/constants.ts";
import { translateOptionName } from "@shared/common/i18n/optionNames";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface PayoutMethodCardProps {
  payoutMethod: PayoutMethod;
  inModal?: boolean;
  /** New look: a row in a flush panel (20px side padding, 56px tall). */
  inPanel?: boolean;
  source: PayoutMethodSource;
}

const PayoutMethodCard = ({
  payoutMethod,
  inModal,
  inPanel,
  source,
}: PayoutMethodCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PAYOUTS);
  const isDarkMode = useSelector(selectIsDarkMode);
  const { isNewLook } = useTheme();
  const payoutMethodType = payoutMethod.payout_method_type;
  const [isEditPayoutModalVisible, setIsEditPayoutModalVisible] =
    useState(false);

  const creationDate = (
    <SmallText>
      {t("payouts.created")}{" "}
      {formatMoment(dateToMoment(payoutMethod.time, true))}
    </SmallText>
  );

  const editModal = (
    <CreateOrEditPayoutMethodModal
      isVisible={isEditPayoutModalVisible}
      closeModal={() => setIsEditPayoutModalVisible(false)}
      isEdit={true}
      payoutMethod={payoutMethod}
      source={source}
    />
  );

  const paymentMethod =
    payoutMethodType === PaymentMethodType.CRYPTO && payoutMethod.asset
      ? CRYPTO_ASSET_TO_DISPLAY[payoutMethod.asset]
      : translateOptionName(PAYOUT_SERVICE_DISPLAY_NAME[payoutMethodType]);

  if (isNewLook) {
    return (
      <>
        <Row $inPanel={inPanel}>
          <LogoTile>{PAYOUT_METHOD_TO_SVG[payoutMethodType]}</LogoTile>
          <RowText>
            <RowName>{payoutMethod.name}</RowName>
            <RowMeta>
              {paymentMethod}
              <span>·</span>
              {t("payouts.created")}{" "}
              {formatMoment(dateToMoment(payoutMethod.time, true))}
            </RowMeta>
          </RowText>
          <EditButton
            type="button"
            aria-label={t("payouts.edit")}
            onClick={() => setIsEditPayoutModalVisible(true)}
          >
            <Pencil size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
          </EditButton>
        </Row>
        {editModal}
      </>
    );
  }

  return (
    <>
      <StyledCard $addBg={inModal && isDarkMode}>
        <PayoutMethodLogo>
          {PAYOUT_METHOD_TO_SVG[payoutMethodType]}
        </PayoutMethodLogo>
        <ContentWrapper>
          <Flex $alignItems="baseline" style={{ marginBottom: 4 }}>
            <Title level={3}>{payoutMethod.name}</Title>
            <EditOutlined onClick={() => setIsEditPayoutModalVisible(true)} />
          </Flex>
          <Flex $alignItems="flex-end">
            <Title level={4} type="secondary">
              {paymentMethod}
            </Title>
            {creationDate}
          </Flex>
        </ContentWrapper>
      </StyledCard>
      {editModal}
    </>
  );
};

export default PayoutMethodCard;

const PAYOUT_METHOD_TO_SVG: {
  [paymentMethod in PaymentMethodType]: React.JSX.Element;
} = {
  [PaymentMethodType.PAYPAL]: <PayPal style={{ fontSize: 36 }} />,
  [PaymentMethodType.CRYPTO]: <CryptoCoins style={{ fontSize: 40 }} />,
};

const StyledCard = styled(Card)<{ $addBg?: boolean }>`
  height: auto;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${(p) =>
    p.$addBg &&
    `
    background: ${p.theme.colors.secondaryCardBgColor};
  `};
`;
const PayoutMethodLogo = styled(Flex)`
  width: 48px;
  margin-right: 12px;
`;
const ContentWrapper = styled.div`
  width: 100%;
`;
const EditOutlined = styled(AntEditOutlined)`
  margin-left: 8px;
  color: ${(p) => p.theme.colors.primaryText};
`;

const Row = styled.div<{ $inPanel?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 12px 0;
  /* 16px from the header / modal title above (12px + 4px). */
  &:first-child {
    padding-top: 4px;
  }
  &:not(:last-child) {
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  ${(p) =>
    p.$inPanel &&
    `
    &&, &&:first-child {
      min-height: 60px;
      padding: 8px 12px 8px 20px;
    }
  `}
`;
const LogoTile = styled.span`
  flex: none;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  > * {
    font-size: 20px !important;
    height: 20px !important;
  }
  svg {
    width: 20px;
    height: 20px;
  }
`;
const RowText = styled.div`
  flex: 1;
  min-width: 0;
`;
const RowName = styled.div`
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const RowMeta = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 2px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  overflow: hidden;
`;
const EditButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: transparent;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  cursor: pointer;
  transition: background-color 120ms, color 120ms;
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).well};
    box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
  @media (max-width: 450px) {
    width: 40px;
    height: 40px;
  }
`;
